import { countries as ibanCountries } from 'iban';

/**
 * Country-aware bank-account field labels (BRAVON-6285).
 *
 * The `iban` package's `countries` registry lists every ISO-2 country that uses the IBAN
 * system, so it answers "does this country use IBAN?" for the whole world without us
 * enumerating anything. Non-IBAN countries fall back to a generic "Account number" + optional
 * "SWIFT/BIC" (the cross-border code commission payouts need). A small override map layers
 * nicer local names on top where a generic label would look wrong.
 *
 * No DB change: the primary value is always stored in the existing `IBAN` field and the
 * secondary in `SWIFT`. For self-contained schemes (CLABE, CBU) the secondary is hidden.
 */

// Self-contained schemes → the local number is enough, no secondary field.
// Account-number schemes → primary "Account number", SWIFT/BIC kept for cross-border payouts.
const LOCAL_BANK_LABELS = {
  MX: { primaryKey: 'bank.label.clabe', secondaryKey: null },
  AR: { primaryKey: 'bank.label.cbu', secondaryKey: null },
  US: { primaryKey: 'bank.label.accountNumber', secondaryKey: 'bank.label.swift' },
  CA: { primaryKey: 'bank.label.accountNumber', secondaryKey: 'bank.label.swift' },
  AU: { primaryKey: 'bank.label.accountNumber', secondaryKey: 'bank.label.swift' },
  IN: { primaryKey: 'bank.label.accountNumber', secondaryKey: 'bank.label.swift' },
};

const IBAN_LABELS = {
  primaryKey: 'bank.label.iban',
  secondaryKey: 'bank.label.swift',
};

const GENERIC_NON_IBAN_LABELS = {
  primaryKey: 'bank.label.accountNumber',
  secondaryKey: 'bank.label.swift',
};

/**
 * Resolve the bank-field labels for a given country.
 * @param {string} [countryCode] ISO-2 country code (e.g. "MX", "ES"). Case-insensitive.
 * @returns {{primaryKey: string, secondaryKey: ?string, secondaryRequired: boolean, usesIban: boolean}}
 */
export default function getBankFieldLabels(countryCode) {
  const cc = (countryCode || '').toUpperCase();

  // No country yet (e.g. profile before billing address) → default to IBAN/SWIFT.
  if (!cc || ibanCountries[cc]) {
    return { ...IBAN_LABELS, secondaryRequired: true, usesIban: true };
  }

  const labels = LOCAL_BANK_LABELS[cc] || GENERIC_NON_IBAN_LABELS;
  return {
    primaryKey: labels.primaryKey,
    secondaryKey: labels.secondaryKey,
    secondaryRequired: false,
    usesIban: false,
  };
}
